import RealmRepresentation from "@keycloak/keycloak-admin-client/lib/defs/realmRepresentation";
import {
  createNamedContext,
  KeycloakSpinner,
  useEnvironment,
  useFetch,
  useRequiredContext,
} from "@keycloak/keycloak-ui-shared";
import { PropsWithChildren, useEffect, useState } from "react";
import { useAdminClient } from "../../admin-client";
import { i18n } from "../../i18n/i18n";
import { useHash } from "./useHash";
import { useMatch } from "react-router-dom";
import {
  DashboardRouteWithRealm,
  DashBoardRouteWithRealmsParam,
} from "../../dashboard/routes/Dashboard";

type RealmContextType = {
  realm: string;
  realmRepresentation?: RealmRepresentation;
  refresh: () => void;
};

export const RealmContext = createNamedContext<RealmContextType | undefined>(
  "RealmContext",
  undefined,
);

export const RealmContextProvider = ({ children }: PropsWithChildren) => {
  const { adminClient } = useAdminClient();
  const { environment } = useEnvironment();
  const [key, setKey] = useState(0);
  const refresh = () => setKey(key + 1);
  const [realmRepresentation, setRealmRepresentation] =
    useState<RealmRepresentation>();

  // --- DA Redirect /realms/:realm to /:realm -------------------------------
  const locationRealm = useHash();
  const routeContainsRealmsParam = window.location.href.includes("/realms/");

  const routeMatch = useMatch({
    path: routeContainsRealmsParam
      ? DashBoardRouteWithRealmsParam.path
      : DashboardRouteWithRealm.path,
    end: false,
  });

  const realm =
    locationRealm.split("/")[1] ||
    routeMatch?.params.realm ||
    environment.realm;
  // --- END DA Redirect /realms/:realm to /:realm ----------------------------

  // Configure admin client to use selected realm when it changes.
  useEffect(() => {
    void (async () => {
      adminClient.setConfig({ realmName: realm });
      const namespace = encodeURIComponent(realm);
      await i18n.loadNamespaces(namespace);
      i18n.setDefaultNamespace(namespace);
    })();
  }, [realm]);
  useFetch(
    () => adminClient.realms.findOne({ realm }),
    setRealmRepresentation,
    [realm, key],
  );

  if (!realmRepresentation) {
    return <KeycloakSpinner />;
  }

  return (
    <RealmContext.Provider value={{ realm, realmRepresentation, refresh }}>
      {children}
    </RealmContext.Provider>
  );
};

export const useRealm = () => useRequiredContext(RealmContext);
